import { X } from 'lucide-react';
import { Button } from '#client/components/ui/Button';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import type { Repeater } from '#client/hooks/useRepeater';
import type { RangeRow } from './parameter-form.logic';
import { GENDER_OPTIONS } from '#client/lib/form-choices';

interface ParameterRangeFieldsProps {
  ranges: Repeater<RangeRow>;
  errors: Record<string, string>;
}

/**
 * The reference ranges a result is judged against, one fieldset per cohort.
 *
 * Index keys are correct here: rows are only appended or removed by index and
 * carry no server identity until the parameter is saved.
 */
export function ParameterRangeFields({ ranges, errors }: ParameterRangeFieldsProps) {
  return (
    <div className="c-form-stack--tight">
      {ranges.rows.map((range, index) => (
        <fieldset className="c-repeater" key={ranges.keys[index]}>
          <legend className="c-repeater__legend">Range {index + 1}</legend>
          {ranges.rows.length > 1 && (
            <Button
              className="c-repeater__remove"
              size="sm"
              variant="ghost"
              icon={<X />}
              aria-label={`Remove range ${index + 1}`}
              onClick={() => ranges.remove(index)}
            />
          )}

          <div className="c-form-grid">
            <TextField
              label="Normal range"
              value={range.normal_range}
              error={errors[`values.${index}.normal_range`]}
              onChange={(event) => ranges.update(index, { normal_range: event.target.value })}
            />
            <TextField
              label="Unit"
              value={range.unit}
              error={errors[`values.${index}.unit`]}
              onChange={(event) => ranges.update(index, { unit: event.target.value })}
            />
            <TextField
              label="Age"
              value={range.age ?? ''}
              onChange={(event) => ranges.update(index, { age: event.target.value })}
            />
            <TextField
              label="Age group"
              value={range.age_group ?? ''}
              onChange={(event) => ranges.update(index, { age_group: event.target.value })}
            />
            <Select
              label="Gender"
              placeholder="Any"
              options={GENDER_OPTIONS}
              value={range.gender ?? ''}
              onChange={(choice) =>
                ranges.update(index, {
                  gender: choice || null,
                })
              }
            />
            <TextField
              label="Other"
              value={range.other ?? ''}
              onChange={(event) => ranges.update(index, { other: event.target.value })}
            />
          </div>
        </fieldset>
      ))}
    </div>
  );
}
